import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, ShieldCheck, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small brand mark (matches Home).
const BrandMark = () => (
  <svg width="28" height="20" viewBox="0 0 26 20" fill="none" aria-hidden="true">
    <path
      d="M2 15C8 15 12 11 15 6C13 12 9 16 3 17C10 18 18 15 23 6"
      stroke="var(--lp-cream)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Centered = ({ children }) => (
  <div className="px-6 pt-28 flex flex-col items-center text-center lp-enter">
    {children}
  </div>
);

// /ring/link — Lashachi sign-in used ONLY to continue Ring account linking.
// Session is checked first; if authenticated we resume claim-link directly.
// Authentication success is NOT the same as Ring connection success.
const RingLink = () => {
  const navigate = useNavigate();
  const { reload } = useApp();
  const [params] = useSearchParams();

  // Ring linking context (preserved in the URL for the whole flow)
  const nonce = params.get('nonce');
  const time = params.get('time');
  const hasRingContext = Boolean(nonce && time);

  const [phase, setPhase] = useState('checking'); // checking|form|authenticating|linking|done|link_error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  // Continue the EXISTING server-side Ring claim-link process.
  const runClaimLink = useCallback(async () => {
    if (!hasRingContext) {
      // Authenticated but no Ring linking context to act on:
      // send to Settings, which shows the TRUE (persisted) Ring state.
      await reload();
      navigate('/settings');
      return;
    }
    setPhase('linking');
    try {
      await axios.post(
        `${API}/integrations/ring/claim-link`,
        { nonce, time },
        { withCredentials: true }
      );
      // Only refresh + reveal A07 once Ring linking actually completed.
      await reload();
      setPhase('done');
      setTimeout(() => navigate('/settings'), 1400);
    } catch (e) {
      setPhase('link_error');
    }
  }, [hasRingContext, nonce, time, reload, navigate]);

  // Session check first.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/session`, { withCredentials: true });
        if (!alive) return;
        if (r.data && r.data.authenticated) {
          await runClaimLink();
        } else {
          setPhase('form');
        }
      } catch (e) {
        if (alive) setPhase('form');
      }
    })();
    return () => {
      alive = false;
    };
  }, [runClaimLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase === 'authenticating') return; // guard duplicate submit
    setError('');
    setPhase('authenticating');
    try {
      await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Do NOT navigate Home — resume the same Ring linking session.
      await runClaimLink();
    } catch (err) {
      // Generic, non-revealing error (email preserved).
      setError('Email or password is incorrect. Please try again.');
      setPhase('form');
    }
  };

  // ---------------------------------------------------------------- states
  if (phase === 'checking') {
    return (
      <Centered>
        <Loader2 size={24} className="animate-spin mb-4" style={{ color: 'var(--lp-body)' }} />
        <p className="font-geist-lp text-[15px]" style={{ color: 'var(--lp-body)' }}>
          Checking your session…
        </p>
      </Centered>
    );
  }

  if (phase === 'linking') {
    return (
      <Centered>
        <Loader2 size={24} className="animate-spin mb-4" style={{ color: 'var(--lp-body)' }} />
        <p className="font-geist-lp text-[15px]" style={{ color: 'var(--lp-body)' }}>
          Connecting Ring…
        </p>
      </Centered>
    );
  }

  if (phase === 'done') {
    return (
      <Centered>
        <CheckCircle2 size={40} className="mb-4" style={{ color: 'var(--lp-sage)' }} />
        <p className="font-serif-lp text-[24px] mb-1" style={{ color: 'var(--lp-cream)' }}>
          Ring connected
        </p>
        <p className="font-geist-lp text-[14px]" style={{ color: 'var(--lp-body)' }}>
          Taking you to Settings…
        </p>
      </Centered>
    );
  }

  if (phase === 'link_error') {
    return (
      <Centered>
        <AlertCircle size={36} className="mb-4" style={{ color: 'var(--lp-body)' }} />
        <h1 className="font-serif-lp text-[26px] mb-2" style={{ color: 'var(--lp-cream)' }}>
          We couldn’t connect Ring
        </h1>
        <p
          className="font-geist-lp text-[15px] leading-relaxed mb-7 max-w-[300px]"
          style={{ color: 'var(--lp-body)' }}
        >
          Your Lashachi session is ready, but we couldn’t finish connecting Ring.
        </p>
        <button
          onClick={runClaimLink}
          className="lp-btn-sage lp-focus font-geist-lp px-8 text-[16px]"
          style={{ minHeight: 48 }}
        >
          Try Again
        </button>
      </Centered>
    );
  }

  // phase === 'form' | 'authenticating'
  const submitting = phase === 'authenticating';

  return (
    <div className="px-6 pt-10 pb-8 lp-enter">
      <div className="flex items-center gap-2 mb-9">
        <BrandMark />
        <span className="font-serif-lp text-[18px]" style={{ color: 'var(--lp-cream)' }}>
          Lashachi <span style={{ color: 'var(--lp-body)' }}>Pet</span>
        </span>
      </div>

      <p className="section-label font-geist-lp uppercase mb-3">Connect with Ring</p>
      <h1 className="font-serif-lp text-[28px] leading-tight mb-2" style={{ color: 'var(--lp-cream)' }}>
        Connect Ring to Lashachi Pet
      </h1>
      <p className="font-geist-lp text-[15px] leading-relaxed mb-5" style={{ color: 'var(--lp-body)' }}>
        Sign in to Lashachi Pet to continue connecting your Ring account.
      </p>

      <div className="lp-card-dark p-4 mb-7">
        <p className="font-geist-lp text-[13px] leading-relaxed" style={{ color: 'var(--lp-muted)' }}>
          Ring provides motion observations that Lashachi Pet can use as evidence
          for possible pet-care activity. You’ll confirm anything Lashachi
          interprets before it becomes a memory.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="font-geist-lp space-y-4" noValidate>
        <div>
          <label htmlFor="lp-email" className="text-[13px] mb-1.5 block" style={{ color: 'var(--lp-muted)' }}>
            Email
          </label>
          <input
            id="lp-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            disabled={submitting}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'lp-auth-error' : undefined}
            className="lp-focus w-full px-4 rounded-xl text-[15px] outline-none"
            style={{
              minHeight: 48,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'var(--lp-cream)',
            }}
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label htmlFor="lp-password" className="text-[13px] mb-1.5 block" style={{ color: 'var(--lp-muted)' }}>
            Password
          </label>
          <div className="relative">
            <input
              id="lp-password"
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'lp-auth-error' : undefined}
              className="lp-focus w-full pl-4 pr-14 rounded-xl text-[15px] outline-none"
              style={{
                minHeight: 48,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'var(--lp-cream)',
              }}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-pressed={showPw}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="lp-focus absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-lg"
              style={{ width: 44, height: 44, color: 'var(--lp-body)' }}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <p
            id="lp-auth-error"
            role="alert"
            className="flex items-start gap-2 text-[13.5px]"
            style={{ color: '#e7c2b2' }}
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="lp-btn-sage lp-focus w-full text-[16px] flex items-center justify-center gap-2"
          style={{ minHeight: 52, opacity: submitting ? 0.75 : 1 }}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Signing in…
            </>
          ) : (
            'Continue'
          )}
        </button>
      </form>

      <p className="font-geist-lp text-[13px] mt-4 text-center" style={{ color: 'var(--lp-muted)' }}>
        Your Ring account will remain separate from your Lashachi account.
      </p>

      <div
        className="flex items-start gap-2.5 mt-8 pt-6"
        style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: 'var(--lp-muted)' }}
      >
        <ShieldCheck size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p className="font-geist-lp text-[12.5px] leading-snug">
          Ring linking is verified securely on our servers. We never store your
          Ring password.
        </p>
      </div>
    </div>
  );
};

export default RingLink;
